import type { JsonPatchOperation } from '@/api/queries/contacts';
import type { Contact } from '@/api/types/contacts';

export type ReviewStatus =
  | 'none'
  | 'requiresReview'
  | 'reviewed'
  | 'autoInvestigation'
  | 'investigated';

export type ReviewChoice =
  | 'none'
  | 'requiresReview:manual'
  | 'requiresReview:automatic'
  | 'autoInvestigation'
  | 'reviewed'
  | 'investigated';

export interface ReviewChange {
  status: ReviewStatus;
  mode?: 'manual' | 'automatic';
}

export interface QuickDraft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  occupation: string;
  interest: string;
  residence: string;
  tags: string[];
  sources: string[];
  rating: string;
  status: 'active' | 'inactive';
  doNotContact: boolean;
  review: ReviewChoice;
}

export type QuickField = keyof QuickDraft;

export type QuickDraftErrors = Partial<Record<QuickField, string>>;

const PROFILE_TEXT_FIELDS = ['position', 'occupation', 'interest', 'residence'] as const;

const REVIEW_CHANGES: Record<ReviewChoice, ReviewChange> = {
  none: { status: 'none' },
  'requiresReview:manual': { status: 'requiresReview', mode: 'manual' },
  'requiresReview:automatic': { status: 'requiresReview', mode: 'automatic' },
  autoInvestigation: { status: 'autoInvestigation', mode: 'automatic' },
  reviewed: { status: 'reviewed', mode: 'manual' },
  investigated: { status: 'investigated' },
};

export const REVIEW_CHOICE_LABEL: Record<ReviewChoice, string> = {
  none: 'Not flagged',
  'requiresReview:manual': 'Requires review (manual)',
  'requiresReview:automatic': 'Requires review (automatic)',
  autoInvestigation: 'Auto investigation',
  reviewed: 'Reviewed',
  investigated: 'Investigated',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function reviewChoiceOf(review?: { status?: string; mode?: string } | null): ReviewChoice {
  switch (review?.status) {
    case 'requiresReview':
      return review.mode === 'automatic' ? 'requiresReview:automatic' : 'requiresReview:manual';
    case 'autoInvestigation':
      return 'autoInvestigation';
    case 'reviewed':
      return 'reviewed';
    case 'investigated':
      return 'investigated';
    default:
      return 'none';
  }
}

export function toQuickDraft(contact: Contact): QuickDraft {
  const score = contact.rating?.score;
  return {
    firstName: contact.profile?.firstName ?? '',
    lastName: contact.profile?.lastName ?? '',
    email: contact.contact?.primaryEmail ?? '',
    phone: contact.contact?.phones?.[0]?.number ?? '',
    position: contact.profile?.position ?? '',
    occupation: contact.profile?.occupation ?? '',
    interest: contact.profile?.interest ?? '',
    residence: contact.profile?.residence ?? '',
    tags: [...(contact.tags ?? [])],
    sources: [...(contact.sources ?? [])],
    rating: score === undefined || score === null ? '' : String(score),
    status: contact.status?.active ? 'active' : 'inactive',
    doNotContact: !!contact.status?.doNotContact,
    review: reviewChoiceOf(contact.review),
  };
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function changedFields(baseline: QuickDraft, draft: QuickDraft): QuickField[] {
  return (Object.keys(baseline) as QuickField[]).filter((key) => {
    const before = baseline[key];
    const after = draft[key];
    if (Array.isArray(before) && Array.isArray(after)) return !sameList(before, after);
    return String(before).trim() !== String(after).trim();
  });
}

export function validateQuickDraft(draft: QuickDraft): QuickDraftErrors {
  const errors: QuickDraftErrors = {};
  const email = draft.email.trim();
  if (!email || !EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address';
  const rating = draft.rating.trim();
  if (rating !== '') {
    const score = Number(rating);
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      errors.rating = 'Rating must be a number between 0 and 100';
    }
  }
  return errors;
}

export function buildQuickPatch(
  baseline: QuickDraft,
  draft: QuickDraft,
  contact: Contact,
): { ops: JsonPatchOperation[]; review?: ReviewChange } {
  const changed = new Set(changedFields(baseline, draft));
  const ops: JsonPatchOperation[] = [];

  if (changed.has('firstName')) {
    ops.push({ op: 'replace', path: '/profile/firstName', value: draft.firstName.trim() });
  }
  if (changed.has('lastName')) {
    ops.push({ op: 'replace', path: '/profile/lastName', value: draft.lastName.trim() });
  }
  for (const field of PROFILE_TEXT_FIELDS) {
    if (changed.has(field)) {
      ops.push({ op: 'replace', path: `/profile/${field}`, value: draft[field].trim() || null });
    }
  }

  if (changed.has('email')) {
    ops.push({ op: 'replace', path: '/contactInfo/primaryEmail', value: draft.email.trim() });
  }

  if (changed.has('phone')) {
    const phone = draft.phone.trim();
    const hasPhone = (contact.contact?.phones?.length ?? 0) > 0;
    if (!phone) {
      if (hasPhone) ops.push({ op: 'remove', path: '/contactInfo/phones/0' });
    } else if (hasPhone) {
      ops.push({ op: 'replace', path: '/contactInfo/phones/0/number', value: phone });
      ops.push({ op: 'replace', path: '/contactInfo/phones/0/normalizationStatus', value: null });
    } else {
      ops.push({
        op: 'replace',
        path: '/contactInfo/phones',
        value: [{ type: 'mobile', number: phone, primary: true }],
      });
    }
  }

  if (changed.has('tags')) ops.push({ op: 'replace', path: '/tags', value: draft.tags });
  if (changed.has('sources')) ops.push({ op: 'replace', path: '/sources', value: draft.sources });

  if (changed.has('rating')) {
    const rating = draft.rating.trim();
    const now = new Date().toISOString();
    if (!rating) {
      ops.push({ op: 'replace', path: '/rating', value: null });
    } else if (contact.rating) {
      ops.push({ op: 'replace', path: '/rating/score', value: Number(rating) });
      ops.push({ op: 'replace', path: '/rating/lastUpdated', value: now });
    } else {
      ops.push({ op: 'replace', path: '/rating', value: { score: Number(rating), lastUpdated: now } });
    }
  }

  if (changed.has('status')) {
    ops.push({ op: 'replace', path: '/status/active', value: draft.status === 'active' });
  }
  if (changed.has('doNotContact')) {
    ops.push({ op: 'replace', path: '/status/doNotContact', value: draft.doNotContact });
  }

  return {
    ops,
    review: changed.has('review') ? REVIEW_CHANGES[draft.review] : undefined,
  };
}
